import { useEffect, useState } from "react";
import { followUser, isFollowingUser, unfollowUser } from "../../services/followService";
import "./FollowButton.css";

/**
 * A reusable follow/unfollow button component
 * 
 * @param {Object} props - Component props
 * @param {string} props.userId - ID of the user to follow/unfollow
 * @param {string} [props.className] - Additional CSS class for styling
 * @param {Function} [props.onFollowChange] - Callback when follow status changes
 * @returns {JSX.Element} Follow button component
 */
const FollowButton = ({ userId, className = "", onFollowChange = () => {} }) => {
  const [isFollowing, setIsFollowing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    
    // Check if the current user is following this user
    const checkFollowStatus = async () => {
      if (!userId) return;
      
      try {
        const isFollowing = await isFollowingUser(userId);
        if (isMounted) {
          setIsFollowing(isFollowing);
        }
      } catch (err) {
        console.error("Error checking follow status:", err);
        if (isMounted) {
          setError("Could not check follow status");
        }
      }
    };

    checkFollowStatus();
    
    return () => {
      isMounted = false;
    };
  }, [userId]);

  const handleToggleFollow = async () => {
    if (isLoading || !userId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      if (isFollowing) {
        // Unfollow the user
        const { error } = await unfollowUser(userId);
        if (error) throw error;
        setIsFollowing(false);
        onFollowChange(false);
      } else {
        // Follow the user
        const { error } = await followUser(userId);
        if (error) throw error;
        setIsFollowing(true);
        onFollowChange(true);
      }
    } catch (err) {
      console.error("Error toggling follow:", err);
      setError(isFollowing ? "Failed to unfollow" : "Failed to follow");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="follow-button-container">
      <button 
        className={`follow-button ${isFollowing ? 'following' : ''} ${className}`}

        style={isFollowing ? { backgroundColor: '#e63946', color: 'white', border: 'none' } : {}}

        onClick={handleToggleFollow}
        disabled={isLoading}
        aria-label={isFollowing ? "Unfollow user" : "Follow user"}
      >
        {isLoading ? (
          <span className="loading-spinner" aria-label="Loading"></span>
        ) : isFollowing ? (
          "Unfollow"
        ) : (
          "Follow"
        )}
      </button>
      {error && <div className="follow-error" role="alert">{error}</div>}
    </div>
  );
};

export default FollowButton; 